import type { FC } from "react";

const documentationHtml = `﻿<!DOCTYPE html>
<html lang="sv">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>ADOne — Systemdokumentation - Exportflöde</title>
  <style>
    :root {
      --primary: #1a3a5c;
      --accent: #e8722a;
      --light-bg: #f5f7fa;
      --border: #d0d7e3;
      --text: #2c2c2c;
      --muted: #6b7280;
      --green: #16a34a;
      --red: #dc2626;
      --blue-light: #eff6ff;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: var(--text); line-height: 1.65; background: #fff; }

    .cover {
      background: linear-gradient(135deg, #1a3a5c 0%, #2b5278 60%, #e8722a 100%);
      color: #fff; padding: 60px 60px 50px; min-height: 240px;
    }
    .cover .back { font-size: 0.85em; opacity: 0.75; margin-bottom: 20px; }
    .cover .back a { color: #fff; text-decoration: underline; }
    .cover h1 { font-size: 2.4em; font-weight: 700; margin-bottom: 10px; }
    .cover .subtitle { font-size: 1.1em; opacity: 0.85; }
    .cover .meta { font-size: 0.85em; opacity: 0.65; margin-top: 30px; }

    .container { max-width: 960px; margin: 0 auto; padding: 40px; }

    .toc { background: var(--light-bg); border-left: 5px solid var(--accent); padding: 24px 32px; margin-bottom: 40px; border-radius: 4px; }
    .toc h2 { color: var(--primary); margin-bottom: 14px; font-size: 1em; text-transform: uppercase; letter-spacing: 0.08em; }
    .toc ol { padding-left: 20px; columns: 2; gap: 30px; }
    .toc li { margin-bottom: 4px; }
    .toc a { color: var(--primary); text-decoration: none; font-size: 0.92em; }
    .toc a:hover { text-decoration: underline; }

    .section { margin-bottom: 52px; }
    h2.section-title { font-size: 1.5em; color: var(--primary); border-bottom: 3px solid var(--accent); padding-bottom: 8px; margin-bottom: 22px; scroll-margin-top: 20px; }
    h3.subsection { font-size: 1.1em; color: var(--primary); margin: 26px 0 10px; padding-left: 10px; border-left: 4px solid var(--border); }
    p { margin-bottom: 12px; }
    ul, ol { padding-left: 22px; margin-bottom: 12px; }
    li { margin-bottom: 5px; }

    .info-box { background: var(--blue-light); border: 1px solid #bfdbfe; border-radius: 6px; padding: 13px 17px; margin: 14px 0; font-size: 0.93em; }
    .warn-box { background: #fffbeb; border: 1px solid #fcd34d; border-radius: 6px; padding: 13px 17px; margin: 14px 0; font-size: 0.93em; }
    .error-box { background: #fff1f2; border: 1px solid #fecdd3; border-radius: 6px; padding: 13px 17px; margin: 14px 0; font-size: 0.93em; }
    .success-box { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; padding: 13px 17px; margin: 14px 0; font-size: 0.93em; }

    table { width: 100%; border-collapse: collapse; margin: 14px 0 22px; font-size: 0.9em; }
    thead th { background: var(--primary); color: #fff; padding: 9px 12px; text-align: left; font-weight: 600; }
    tbody tr:nth-child(even) { background: var(--light-bg); }
    tbody td { padding: 8px 12px; border-bottom: 1px solid var(--border); vertical-align: top; }

    .badge { display: inline-block; padding: 2px 9px; border-radius: 12px; font-size: 0.8em; font-weight: 600; margin: 1px; }
    .badge-blue   { background: #dbeafe; color: #1d4ed8; }
    .badge-orange { background: #ffedd5; color: #c2410c; }
    .badge-green  { background: #dcfce7; color: #166534; }
    .badge-red    { background: #ffe4e6; color: #be123c; }
    .badge-gray   { background: #f3f4f6; color: #374151; }
    .badge-yellow { background: #fef9c3; color: #854d0e; }

    .mono { font-family: 'Consolas', monospace; font-size: 0.88em; background: var(--light-bg); padding: 1px 5px; border-radius: 3px; }

    /* Flow box */
    .flow-box { font-family: 'Consolas', 'Courier New', monospace; font-size: 0.82em; background: #1e293b; color: #e2e8f0; padding: 20px 24px; border-radius: 6px; margin: 16px 0 22px; white-space: pre; overflow-x: auto; line-height: 1.6; }

    /* Code block */
    .code-block { font-family: 'Consolas', 'Courier New', monospace; font-size: 0.83em; background: #f8fafc; border: 1px solid var(--border); color: #1e293b; padding: 16px 20px; border-radius: 6px; margin: 12px 0 18px; white-space: pre; overflow-x: auto; line-height: 1.6; }

    /* Scenario cards */
    .scenario-card { border: 1px solid var(--border); border-radius: 8px; margin: 18px 0; overflow: hidden; }
    .scenario-card .card-header { padding: 11px 18px; font-weight: 700; font-size: 0.95em; display: flex; align-items: center; gap: 10px; }
    .scenario-card .card-header.success { background: #f0fdf4; border-bottom: 1px solid #bbf7d0; color: #166534; }
    .scenario-card .card-header.error   { background: #fff1f2; border-bottom: 1px solid #fecdd3; color: #be123c; }
    .scenario-card .card-header.warn    { background: #fffbeb; border-bottom: 1px solid #fcd34d; color: #92400e; }
    .scenario-card .card-header.info    { background: var(--blue-light); border-bottom: 1px solid #bfdbfe; color: #1e40af; }
    .scenario-card .card-body { padding: 14px 18px; font-size: 0.91em; }

    /* Inline flow steps */
    .flow { display: flex; align-items: center; flex-wrap: wrap; gap: 0; margin: 16px 0; }
    .flow-step { background: var(--primary); color: #fff; padding: 9px 16px; border-radius: 4px; font-size: 0.85em; text-align: center; min-width: 90px; }
    .flow-arrow { font-size: 1.3em; color: var(--accent); padding: 0 5px; }

    .page-break { page-break-before: always; }

    .doc-footer { background: var(--light-bg); border-top: 3px solid var(--primary); padding: 20px 40px; font-size: 0.85em; color: var(--muted); text-align: center; margin-top: 60px; }

    #backToTop { position: fixed; bottom: 24px; right: 24px; background: var(--primary); color: #fff; padding: 9px 14px; border-radius: 6px; font-size: 0.85em; font-weight: 600; text-decoration: none; display: none; z-index: 100; }
    #backToTop:hover { background: var(--accent); }
  </style>
</head>
<body>

<a href="#top" id="backToTop">↑ Till toppen</a>

<div class="cover" id="top">
  <div class="back">← <a href="/admin/documentation" target="_top">Tillbaka till huvuddokumentation</a></div>
  <h1>Exportflöde till AX</h1>
  <div class="subtitle">Detaljerad dokumentation — <code>ExportOrderAsync</code> — ADOne Backend API</div>
  <div class="meta">AD Sverige &bull; Teknisk dokumentation &bull; September 2026 &bull; Komplexitet: Mycket hög</div>
</div>

<div class="container">

  <div class="toc">
    <h2>Innehållsförteckning</h2>
    <ol>
      <li><a href="#overview">Översikt</a></li>
      <li><a href="#main-flow">Huvudflöde (steg-för-steg)</a></li>
      <li><a href="#order-types">Ordertyperna — Standard, Catalog, Fleet</a></li>
      <li><a href="#message-matrix">Meddelandematris</a></li>
      <li><a href="#scenarios-ok">Lyckade exportscenarier</a></li>
      <li><a href="#scenarios-fail">Misslyckade exportscenarier</a></li>
      <li><a href="#state">Order-tillståndsövergångar (State Transitions)</a></li>
      <li><a href="#errors">Feltyper och exceptions</a></li>
      <li><a href="#config">Konfigurationskrav</a></li>
      <li><a href="#diagnostics">Snabbdiagnostik vid fel</a></li>
    </ol>
  </div>


  <!-- 1. OVERVIEW -->
  <div class="section" id="overview">
    <h2 class="section-title">1. Översikt</h2>
    <p>
      <strong>ExportOrderAsync</strong> är funktionen i backend-API:et som exporterar en order (med tillhörande artiklar)
      till <strong>Microsoft Dynamics AX</strong> via Azure Service Bus och projektet AX Communication.
      API:et mappar ordern till JSON och skickar den till en kö. AX Communication hämtar meddelandet,
      mappar det till CSV och lägger det på SFTP där AX hämtar ordern.
    </p>

    <h3 class="subsection">Vad funktionen gör</h3>
    <ul>
      <li>API:et validerar konfiguration och bygger exportdata per ordertyp</li>
      <li>API:et mappar order- och artikeldata till ett JSON-meddelande och publicerar det till AX Communication</li>
      <li>AX Communication mappar mottagen order till en order-CSV och vid behov en separat artikel-CSV</li>
      <li>AX Communication överför CSV-filerna via SFTP till AX</li>
      <li>AX Communication pollar efter AX-svar och skickar mappat svar till API:et via en annan kö</li>
      <li>API:ets <span class="mono">OrderWorkerService</span> tar emot AX-svaret, matchar order och artiklar via GUID i <span class="mono">ExternalId</span>, uppdaterar FO/status och fyller artikelradernas IO/FO</li>
    </ul>

    <div class="info-box">
      <strong>Viktigt att förstå:</strong> API:et skickar inte CSV-filer till AX. Det skickar ett JSON-meddelande
      till Service Bus. AX Communication skapar CSV-filerna och hanterar SFTP-utbytet med AX. En separat
      AX Communication-worker pollar svarssökvägen var tionde sekund och skickar svar till API:et via en annan kö.
      API:ets <span class="mono">OrderWorkerService</span> behandlar svaret asynkront; API:ets <span class="mono">Exported</span>-flagga betyder att meddelandet publicerats, inte att AX har bekräftat ordern.
    </div>
  </div>


  <!-- 2. MAIN FLOW -->
  <div class="section" id="main-flow">
    <h2 class="section-title">2. Huvudflöde (steg-för-steg)</h2>
    <div class="flow-box">START
  │
  ├─→ [Steg 1] Validera exportkonfiguration (AX- och Service Bus-inställningar)
  │    ├─→ Saknas config? 🔴 Exception kastas omedelbart
  │    └─→ OK? ✅ Fortsätt
  │
  ├─→ [Steg 2] Logga användarinfo och order-ID
  │
  ├─→ [Steg 3] Hämta ordern från databasen
  │
  ├─→ [Steg 4] Kontrollera: Pågår redan en export?
  │    ├─→ JA (order.Exporting == true)? ⚠️ Avbryt (return)
  │    └─→ NEJ? ✅ Fortsätt
  │
  ├─→ [Steg 5] Markera ordern: order.Exporting = true
  │
  ├─→ [Steg 6] Bygg exportdata (BuildExportDataAsync)
  │    ├─→ Iterera alla artiklar med Quantity > 0
  │    ├─→ Validera priser per ordertyp
  │    ├─→ Validera leverantörer (Standard/Catalog)
  │    ├─→ Kontrollera lagerstatus via ADI (Fleet)
  │    ├─→ ADI-enrichment för artikel-ID (Fleet/Catalog)
  │    ├─→ Exception? 🔴 Fångas och kastas vidare
  │    └─→ OK? ✅ Fortsätt
  │
  ├─→ [Steg 7] Mappa ordern till ett ExportOrder-JSON-meddelande
  │    └─→ Kund, leverans, integrationsdata och artiklar ingår i samma meddelande
  │
  ├─→ [Steg 8] API skickar meddelandet till en Service Bus-kö
  │
  ├─→ [Steg 9] Markera ordern som exporterad när API:et accepterat köpubliceringen
  │
  └─→ [Finally-block — körs ALLTID, även vid exception]
       └─→ Återställ: order.Exporting = false</div>

    <h3 class="subsection">Asynkront AX-svar (efter API-anropet)</h3>
    <div class="flow">
      <div class="flow-step">AX Communication hämtar JSON från kön</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Mappar ordern till CSV och lägger den på SFTP</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">AX hämtar ordern och lämnar svar på SFTP</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">OrderResultWorkerService pollar svarssökvägen var 10:e sekund</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">AX Communication publicerar JSON-svar i OrderResultSender-kön</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">API konsumerar svaret och uppdaterar IO och FO</div>
    </div>

    <div class="info-box">
      <strong>Verifierad ansvarsfördelning:</strong> <span class="mono">OrderResultWorkerService</span> i AX Communication
      är en SFTP-poller, inte en Service Bus-lyssnare. API:ets <span class="mono">OrderWorkerService</span> lyssnar på svarskön,
      deserialiserar <span class="mono">ImportOrder</span> och skickar uppdateringen vidare till <span class="mono">CommonOrderService</span>.
    </div>

    <div class="warn-box">
      <strong>Finally-blocket är kritiskt:</strong> Oavsett om exporten lyckas eller misslyckas,
      återställs <span class="mono">order.Exporting</span> till <span class="mono">false</span> i ett
      finally-block. Detta garanterar att ordern inte fastnar i ett "under export"-tillstånd.
    </div>

  </div>


  <!-- 3. ORDER TYPES -->
  <div class="section page-break" id="order-types">
    <h2 class="section-title">3. Ordertyperna — Standard, Catalog, Fleet</h2>
    <p>
      Exportbeteendet skiljer sig markant mellan de tre klienttyperna. Nedan en
      jämförelse av de viktigaste skillnaderna:
    </p>

    <table>
      <thead>
        <tr>
          <th>Egenskap</th>
          <th><span class="badge badge-blue">Standard</span></th>
          <th><span class="badge badge-orange">Catalog (AD Butik)</span></th>
          <th><span class="badge badge-green">Fleet</span></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Kund krävs</strong></td>
          <td>Ja</td>
          <td>Ja</td>
          <td><strong>Ja — obligatoriskt</strong></td>
        </tr>
        <tr>
          <td><strong>Leverantör per artikel</strong></td>
          <td>Ja — obligatoriskt</td>
          <td>Ja — obligatoriskt</td>
          <td>Nej (valfritt)</td>
        </tr>
        <tr>
          <td><strong>Priskrav</strong></td>
          <td>SalesPrice, StoreSalesPrice, WorkshopSalesPrice, PurchasePrice — alla ≥ 1</td>
          <td>Samma som Standard</td>
          <td>Endast SalesPrice ≥ 1</td>
        </tr>
        <tr>
          <td><strong>Lagerkontroll (ADI)</strong></td>
          <td>Nej</td>
          <td>Nej</td>
          <td>Ja</td>
        </tr>
        <tr>
          <td><strong>ADI-enrichment</strong></td>
          <td>Nej</td>
          <td>Ja — ItemId, ItemGroupId</td>
          <td>Ja — ItemId, ItemGroupId, InventLocationID</td>
        </tr>
        <tr>
          <td><strong>Orderidentifierare i JSON-kontraktet</strong></td>
          <td><span class="mono">FileIdentifier = AONumber.ToString()</span></td>
          <td><span class="mono">FileIdentifier = ExternalOrderId</span></td>
          <td><span class="mono">FileIdentifier = ClientExternalOrderId</span></td>
        </tr>
        <tr>
          <td><strong>Extra orderdata i JSON</strong></td>
          <td>Order-, kund-, leverans- och artikeldata</td>
          <td>Warehouse, SalesPrice och PurchasePrice</td>
          <td>CustomerNumber, leveransuppgifter, Warehouse och OrderType</td>
        </tr>
      </tbody>
    </table>

    <h3 class="subsection">Identifierare och externa ID:n</h3>
    <p>
      <span class="mono">FileIdentifier</span> sätts av API:t per klienttyp: Standard använder
      <span class="mono">AONumber.ToString()</span>, Catalog använder <span class="mono">ExternalOrderId</span> och Fleet använder
      <span class="mono">ClientExternalOrderId</span>. Det senare är alltså inte samma property som Catalogs order-ID.
      <span class="mono">ExternalId</span> är ett separat fält på order, artikel och kund och innehåller respektive ADOne-GUID
      som används för att matcha AX-svaret tillbaka till rätt post. Detta ska skiljas från
      <span class="mono">Supplier.ExternalId</span>, som är leverantörens externa AX-ID i exportvalideringen.
    </p>
    <ul>
      <li><span class="mono">ExportOrder.ExternalId = order.Id</span></li>
      <li><span class="mono">ExportArticle.ExternalId = article.Id</span></li>
      <li><span class="mono">ExportCustomer.ExternalId = customer.Id</span></li>
    </ul>

    <h3 class="subsection">CSV-filer och SFTP (AX Communication)</h3>
    <p>
      Det är AX Communication, inte API:et, som skapar CSV-filerna.
      Om ordern innehåller artiklar markerade <span class="mono">NewArticle = true</span> skapas dessutom en separat artikel-fil.
      Sökvägarna <span class="mono">ArticlePath</span>, <span class="mono">OrderPath</span> och
      <span class="mono">OrderResultPath</span> konfigureras per integration i AX Communications databas.
    </p>
    <table>
      <thead><tr><th>Fil</th><th>När den skapas</th><th>Format</th></tr></thead>
      <tbody>
        <tr><td><span class="mono">article_{RegistrationNumber}_{FileIdentifier}_{timestamp}.csv</span></td><td>När ordern innehåller nya artiklar (<span class="mono">NewArticle = true</span>)</td><td>Semikolonseparerad, Windows-1252, ingen header</td></tr>
        <tr><td><span class="mono">order_{RegistrationNumber}_{FileIdentifier}_{timestamp}.csv</span></td><td>För orderns artikeldata</td><td>Semikolonseparerad, Windows-1252, ingen header</td></tr>
      </tbody>
    </table>

    <h3 class="subsection">AX-svar och leverantörsimport (AX Communication)</h3>
    <p>
      <span class="mono">OrderResultWorkerService</span> pollar integrationens <span class="mono">OrderResultPath</span> på SFTP
      var tionde sekund. Den läser TXT-svar, matchar order och artiklar mot sparad integrationsdata och skickar ett mappat JSON-svar
      till <span class="mono">QueueConnections:OrderResultSender</span>. API:ets <span class="mono">OrderWorkerService.ProcessMessageAsync</span>
      lyssnar via <span class="mono">AXCommunicatorConfiguration.OrderReciever</span>, deserialiserar <span class="mono">ImportOrder</span>
      och matchar ordern via dess GUID i <span class="mono">ExternalId</span>. <span class="mono">CommonOrderService.UpdateOrderAfterAXImport</span>
      sätter orderns FO (<span class="mono">SalesOrderNumber</span>) och AX-callbackstatus. Artikelrader matchas via artikelns GUID i
      <span class="mono">ExternalId</span>; IO (<span class="mono">PurchaseOrderNumber</span>) och FO
      (<span class="mono">SalesOrderNumber</span>) uppdateras bara när respektive fält är tomt.
    </p>
    <p>
      Leverantörsimporten börjar med att API:t skickar ett trigger-meddelande till
      <span class="mono">QueueConnections:SupplierListener</span>. AX Communications <span class="mono">SupplierWorkerService</span>
      hämtar leverantörsfilen via SFTP, mappar leverantörerna och skickar hela listan som ett samlat JSON-meddelande till
      <span class="mono">QueueConnections:SupplierSender</span>. API:ets supplier-consumer tar emot den samlade listan och uppdaterar leverantörsregistret.
    </p>
    <div class="warn-box">
      AX Communication tar bort sin behandlade orderpost ur sin egen databas efter att AX-svaret hanterats och skapar en arkiverad kopia för framtida referens.
    </div>

    <h3 class="subsection">ADI-enrichment (Catalog och Fleet)</h3>
    <p>
      API:et anropar ADI för att berika artikeldata inför export till AX. Processen ser ut så här:
    </p>
    <div class="flow">
      <div class="flow-step">Artikel med leverantör</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Anropar ADI API</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Hämtar ItemId, ItemGroupId (+ lagerplats för Fleet)</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Sparar till artikel i DB</div>
    </div>
    <div class="info-box">
      ADI-enrichment mappar <span class="mono">ItemId</span> till <span class="mono">article.AdId</span> och
      <span class="mono">ItemGroupId</span> till artikelgrupp. För Fleet mappas även <span class="mono">InventLocationID</span>
      till lagerplats.
    </div>
  </div>


  <!-- 4. MESSAGE MATRIX -->
  <div class="section" id="message-matrix">
    <h2 class="section-title">4. Meddelandematris</h2>
    <p>
      Ett JSON-meddelande skapas per godkänd export. Meddelandet innehåller
      orderdata och artiklar som passerat API-valideringen. AX Communication ansvarar därefter för CSV- och SFTP-steget.
    </p>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Scenario</th>
          <th>JSON till kö</th>
          <th>Innehåll/utfall</th>
          <th>Utfall</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>1</td><td>Standard Order (lyckad)</td><td><span class="badge badge-green">✓ Ja</span></td><td>Orderdata och artiklar som klarat valideringen; <span class="mono">FileIdentifier</span> från <span class="mono">AONumber</span></td><td>Ordern markeras exporterad; AX Communication skapar CSV-filer och placerar dem på SFTP</td></tr>
        <tr><td>2</td><td>Catalog Order (lyckad)</td><td><span class="badge badge-green">✓ Ja</span></td><td>Orderdata, godkända artiklar och ADI-berikning; <span class="mono">FileIdentifier</span> från <span class="mono">ExternalOrderId</span></td><td>Ordern exporteras med Catalog-data</td></tr>
        <tr><td>3</td><td>Fleet Order (lyckad)</td><td><span class="badge badge-green">✓ Ja</span></td><td>Order-, kund-, leverans- och artikeldata; <span class="mono">FileIdentifier</span> från <span class="mono">ClientExternalOrderId</span></td><td>Ordern exporteras efter nödvändiga valideringar och lagerkontroller</td></tr>
        <tr><td>4</td><td>Saknad AX-konfiguration</td><td><span class="badge badge-red">✗ Nej</span></td><td>Tjänsten kan inte instansieras med obligatorisk konfiguration</td><td>Fel uppstår vid applikationsstart/DI, innan exportanropet och utan databasändring</td></tr>
        <tr><td>5</td><td>Priser saknas (Standard/Catalog)</td><td><span class="badge badge-red">✗ Nej</span></td><td>Minst ett obligatoriskt artikelpris är under 1</td><td>Exporten avbryts med exception; <span class="mono">order.Exported</span> lämnas oförändrad</td></tr>
        <tr><td>6</td><td><span class="mono">SalesPrice</span> saknas (Fleet)</td><td><span class="badge badge-red">✗ Nej</span></td><td>Minst en artikel med antal över 0 har <span class="mono">SalesPrice &lt; 1</span></td><td>Exporten avbryts med exception</td></tr>
        <tr><td>7</td><td>Kund saknas (Fleet)</td><td><span class="badge badge-red">✗ Nej</span></td><td><span class="mono">order.Customer</span> saknas</td><td>Exporten avbryts med exception eftersom leveransuppgifter krävs</td></tr>
        <tr><td>8</td><td>Lagerinformation saknas (Fleet)</td><td><span class="badge badge-red">✗ Nej</span></td><td>ADI returnerar ingen lagerinformation för en artikel utan egen leverantör som inte är en artikelmall</td><td>Exporten avbryts; fel om saknad lagerinformation</td></tr>
        <tr><td>9</td><td>Otillräcklig lagerstatus (Fleet)</td><td><span class="badge badge-red">✗ Nej</span></td><td>ADI-lagret räcker inte för artikelns antal och undantaget för artikelgrupp 3 gäller inte</td><td>Exporten avbryts; fel om otillräckligt lager</td></tr>
        <tr><td>10</td><td>Leverantör saknas eller är exkluderad (Standard/Catalog)</td><td><span class="badge badge-yellow">⚠ Ja, eventuellt partiellt</span></td><td>Artikeln hoppas över; ordern och övriga godkända artiklar skickas</td><td>Ordern kan markeras exporterad trots att en eller flera artiklar saknas i meddelandet</td></tr>
        <tr><td>11</td><td>Lagerplats <span class="mono">"3"</span> med egen leverantör (Fleet)</td><td><span class="badge badge-green">✓ Ja, om övriga villkor är uppfyllda</span></td><td>Leverantörens externa ID används i <span class="mono">SupplierNumber</span>; warehouse-värdet bestäms separat av warehouse-reglerna</td><td>Specialhantering av artikelraden; i sig stoppar den inte exporten</td></tr>
        <tr><td>12</td><td><span class="mono">ExcludeFromWarehousePicking</span> (Fleet)</td><td><span class="badge badge-green">✓ Ja, om övriga villkor är uppfyllda</span></td><td>Artikelnumret ersätts; <span class="mono">SupplierNumber</span> och <span class="mono">Warehouse</span> blir tomma, <span class="mono">PurchasePrice</span> blir <span class="mono">null</span> och kommentaren byggs av artikelnummer och beskrivning</td><td>Specialhantering av artikelraden; i sig stoppar den inte exporten</td></tr>
      </tbody>
    </table>

    <div class="warn-box">
      <strong>Scenario 10 — viktigt:</strong> Om en artikel saknar giltig leverantör (Standard/Catalog)
      hoppas <em>den artikeln</em> över, men ordern markeras ändå som <span class="mono">Exported = true</span>.
      Här betyder <span class="mono">Exported</span> att det partiella JSON-meddelandet publicerades till Service Bus;
      det betyder inte att AX har bekräftat ordern.
    </div>
  </div>


    <!-- 5. SUCCESSFUL SCENARIOS -->
  <div class="section" id="scenarios-ok">
    <h2 class="section-title">5. Lyckade exportscenarier</h2>

    <div class="scenario-card">
      <div class="card-header success">Scenario 1 — Standard Order (lyckad)</div>
      <div class="card-body">
        <strong>Villkor:</strong>
        <ul>
          <li>ClientType = Standard</li>
          <li>Ordern har minst en artikel</li>
          <li>Alla artiklar (Quantity &gt; 0): SalesPrice, StoreSalesPrice, WorkshopSalesPrice, PurchasePrice — alla ≥ 1</li>
          <li>Varje artikel har en leverantör som <em>inte</em> är exkluderad från export och har ett giltigt <span class="mono">Supplier.ExternalId</span> (AX-leverantörs-ID)</li>
        </ul>
        <strong>Resultat:</strong>
        <ul>
          <li>API:et publicerar ett JSON-meddelande med order- och artikeldata. AX Communication mappar sedan meddelandet till CSV och placerar filerna på SFTP.</li>
          <li><span class="mono">order.Exported = true</span></li>
          <li><span class="mono">article.AdId</span> sparas i databasen</li>
        </ul>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header success">Scenario 2 — Catalog Order (lyckad)</div>
      <div class="card-body">
        <strong>Villkor:</strong>
        <ul>
          <li>ClientType = Catalog</li>
          <li>Samma priskrav som Standard</li>
          <li>Leverantör krävs per artikel</li>
        </ul>
        <strong>Tillägg jämfört med Standard:</strong>
        <ul>
          <li>ADI-enrichment körs per artikel — hämtar <span class="mono">ItemId</span> och <span class="mono">ItemGroupId</span> från ADI API</li>
          <li>JSON-meddelandet innehåller Catalog-data med Warehouse, SalesPrice och PurchasePrice; AX Communication mappar innehållet till AX-formatet.</li>
          <li><span class="mono">Warehouse</span> (på ordernivå) hämtas från <span class="mono">AX:CatalogOrderWarehouse</span>, medan <span class="mono">NewArticleWarehouse</span> hämtas från <span class="mono">AX:CatalogWarehouse</span> — två olika konfigurationsvärden används parallellt.</li>
          <li>Priser sätts via <span class="mono">BrandDiscount</span> om en sådan finns för bilmärket: <span class="mono">StoreSalesPrice</span>/<span class="mono">WorkshopSalesPrice</span> beräknas med <span class="mono">CalculateDiscountedPrice(SalesPrice, discount)</span>. Annars sätts båda lika med <span class="mono">SalesPrice</span>.</li>
        </ul>
      </div>
    </div>

<div class="scenario-card">
  <div class="card-header success">Scenario 3 — Fleet Order (lyckad)</div>
  <div class="card-body">
    <strong>Villkor:</strong>
    <ul>
      <li>ClientType = Fleet</li>
      <li><span class="mono">order.Customer</span> måste existera (leveransadress hämtas därifrån)</li>
      <li>SalesPrice ≥ 1 för varje artikel (enda priskravet)</li>
      <li>Lagerstatus OK enligt ADI (om artikeln inte är en artikelmall och inte har egen leverantör)</li>
    </ul>
    <strong>Tillägg:</strong>
    <ul>
      <li>ADI-enrichment: ItemId, ItemGroupId <em>och</em> InventLocationID (lagerplats) hämtas</li>
      <li>JSON-meddelandet innehåller leveransinformation från kunden:
        DeliveryName (från <span class="mono">order.WorkshopName</span>), DeliveryAddress, PostalCode, City</li>
      <li>CustomerNumber och OrderType hämtas från konfiguration (<span class="mono">_fleetCustomerNumber</span>, <span class="mono">_fleetOrderTypeName</span>) och följer med i meddelandet</li>
      <li>Komplex warehouse-logik med 7 olika grenar i <span class="mono">GetOrderWarehouse()</span></li>
      <li><span class="mono">PurchasePrice</span> kan bli <span class="mono">null</span> i exportmeddelandet för artikelmallar, <span class="mono">ExcludeFromWarehousePicking</span>-artiklar, och artiklar med <span class="mono">WarehouseLocation == "3"</span></li>
    </ul>

    <strong>Så bestäms warehouse-värdet:</strong>
    <p>Reglerna kontrolleras uppifrån och ned. Det första villkor som stämmer bestämmer värdet som skickas i exporten.</p>
    <table>
      <thead>
        <tr><th>#</th><th>Kontrollera</th><th>Värde i exporten</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>1</td>
          <td>Artikeln är undantagen från lagerplockning: <span class="mono">ExcludeFromWarehousePicking = true</span>.</td>
          <td>Tomt värde (<span class="mono">""</span>)</td>
        </tr>
        <tr>
          <td>2</td>
          <td>Artikeln matchar en artikelmall: <span class="mono">isArticleTemplate = true</span>.</td>
          <td>Tomt värde (<span class="mono">""</span>)</td>
        </tr>
        <tr>
          <td>3</td>
          <td><span class="mono">Stock &gt; 0</span>, och det är inte samtidigt en egen leverantör och lagerplats <span class="mono">"3"</span>.</td>
          <td><span class="mono">"3"</span></td>
        </tr>
        <tr>
          <td>4</td>
          <td>Lagerplatsen är <span class="mono">"3"</span>, artikeln saknar egen leverantör och regel 3 stämde inte.</td>
          <td><span class="mono">"3"</span></td>
        </tr>
        <tr>
          <td>5</td>
          <td>Leverantören har ett eget lager angivet: <span class="mono">Supplier.Warehouse</span> är inte tomt.</td>
          <td><span class="mono">Supplier.Warehouse</span></td>
        </tr>
        <tr>
          <td>6</td>
          <td>Artikeln har en lagerplats angiven, men leverantören saknar eget lager.</td>
          <td><span class="mono">Warehouse.BES</span></td>
        </tr>
        <tr>
          <td>7</td>
          <td>Ingen av reglerna ovan stämmer.</td>
          <td><span class="mono">Warehouse.RAT</span> (standardvärde)</td>
        </tr>
      </tbody>
    </table>

    <div class="info-box">
      <strong>Specialfall:</strong> Om artikeln har lagerplats <span class="mono">"3"</span> och en egen leverantör,
      hoppas reglerna 3 och 4 över. Finns <span class="mono">Supplier.Warehouse</span> används det värdet enligt regel 5.
      Annars fortsätter kontrollen till reglerna 6 och 7. I koden styrs detta av
      <span class="mono">useSpecifiedSupplierWarehouse</span>.
    </div>
  </div>
</div>

  </div>


  <!-- 6. FAILURE SCENARIOS -->
  <div class="section page-break" id="scenarios-fail">
    <div class="scenario-card">
      <div class="card-header error">Scenario 4 — Saknad AX-konfiguration</div>
      <div class="card-body">
        <p><strong>Villkor:</strong> En eller flera av dessa saknas i konfigurationen när respektive klienttypstjänst skapas (konstruktorn, via <span class="mono">ConfigurationHelper.GetRequiredConfigurationValue</span>):
          <span class="mono">AX:ArticleGroup</span>, <span class="mono">AX:DefaultWarehouse</span> (Standard),
          <span class="mono">AX:CatalogArticleGroup</span>, <span class="mono">AX:CatalogWarehouse</span>, <span class="mono">AX:CatalogOrderWarehouse</span>, <span class="mono">AXCommunicator:IdentificationNameCatalog</span> (Catalog)
        </p>
        <p><strong>Resultat:</strong> Exception kastas redan <em>vid applikationsstart / DI-uppsättning</em> (när tjänsten instansieras), inte vid själva export-anropet. Ingen databasändring görs eftersom export aldrig når så långt.</p>
        <p><strong>Åtgärd:</strong> Kontrollera API-projektets konfiguration och miljövariabler i rätt miljö innan deployment.</p>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header error">Scenario 5 — Priser saknas (Standard/Catalog)</div>
      <div class="card-body">
        <p><strong>Villkor:</strong> En eller flera av dessa är &lt; 1 på någon artikel:
          <span class="mono">SalesPrice</span>, <span class="mono">StoreSalesPrice</span>,
          <span class="mono">WorkshopSalesPrice</span>, <span class="mono">PurchasePrice</span>
        </p>
        <p><strong>Loggmeddelande:</strong> <em>"Aborting export request because all prices are not set."</em></p>
        <p><strong>Resultat:</strong> <span class="mono">Exception("Aborting export request because all prices are not set.")</span> kastas (i <span class="mono">OrderServiceCore.PrepareArticleForExportAsync</span>). <span class="mono">order.Exported</span> oförändrad.</p>
        <p><strong>Åtgärd:</strong> Kontrollera alla artikelpriser i ordervyn och sätt de priser som saknas.</p>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header error">Scenario 6 — SalesPrice saknas (Fleet)</div>
      <div class="card-body">
        <p><strong>Villkor:</strong> <span class="mono">SalesPrice &lt; 1</span> på någon artikel med Quantity &gt; 0 (Fleet-order)</p>
        <p><strong>Notering:</strong> Fleet kontrollerar <em>bara</em> SalesPrice, inte de andra priserna.</p>
        <p><strong>Loggmeddelande:</strong> <em>"Aborting export request because sales price is not set for fleet order."</em></p>
        <p><strong>Resultat:</strong> <span class="mono">Exception("No sales price", "Aborting export request because sales price is not set for fleet order.")</span> kastas.</p>
        <p><strong>Åtgärd:</strong> Sätt SalesPrice på alla artiklar.</p>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header error">Scenario 7 — Kund saknas (Fleet)</div>
      <div class="card-body">
        <p><strong>Villkor:</strong> <span class="mono">order.Customer == null</span> (Fleet-order)</p>
        <p><strong>Varför:</strong> Fleet-orderinformationen som skickas vidare kräver leveransuppgifter (namn, adress, postnummer, stad) från kunden.</p>
        <p><strong>Resultat (ändrat):</strong> <span class="mono">Exception("Customer information is missing for the order.", "Cannot export order without customer information.")</span> kastas.</p>
        <p><strong>Åtgärd:</strong> Länka en kund till Fleet-ordern innan export.</p>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header error">Scenario 8 — Ingen lagerinformation hittad (Fleet)</div>
      <div class="card-body">
        <p><strong>Villkor:</strong></p>
        <ul>
          <li>Fleet-order</li>
          <li>Artikeln saknar egen leverantör (<span class="mono">articleSupplier == null</span>) och är inte en artikelmall</li>
          <li><span class="mono">stock == null</span> eller tom lista från ADI</li>
        </ul>
        <p><strong>Loggmeddelande:</strong> <em>"No stock information found for article with ADID [adId], cannot export order"</em></p>
        <p><strong>Exception:</strong> <span class="mono">Exception("No stock information found for article with ADID [adId], cannot export order.")</span></p>
        <p><strong>Åtgärd:</strong> Kontrollera varför ADI inte returnerar lagerdata för artikeln.</p>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header error">Scenario 9 — Otillräcklig lagerstatus (Fleet)</div>
      <div class="card-body">
        <p><strong>Villkor:</strong></p>
        <ul>
          <li>Fleet-order</li>
          <li>Artikeln saknar egen leverantör och är inte en artikelmall</li>
          <li>Lagerdata finns, men <span class="mono">(article.Quantity / 100) &gt; stockInfo.Stock</span></li>
          <li>Undantag gäller när artikelns berikade <span class="mono">ItemGroupId</span> (från ADI, ej artikelns egen <span class="mono">WarehouseLocation</span>) matchar konfigurationsvärdet <span class="mono">InStockInventLocationId</span> ("lager 3") — då hoppas lagerkontrollen över helt</li>
        </ul>
        <p><strong>Exception:</strong> <span class="mono">Exception</span> kastas med meddelande:
          <em>"Article with ADID [id] has insufficient stock according to ADI (Stock: X, Quantity: Y), cannot export order."</em></p>
        <p><strong>Åtgärd:</strong> Kontrollera ADI-lagerdata och artikelns leverantör, mallstatus och artikelgrupp. Minska antalet om det överstiger tillgängligt lager.</p>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header warn">Scenario 10 — Leverantör saknas/exkluderad (Standard/Catalog) — partiell export</div>
      <div class="card-body">
        <p><strong>Villkor:</strong> En artikel saknar leverantör, eller leverantören har
          <span class="mono">IsExcludedFromExport = true</span>, eller leverantörens <span class="mono">ExternalId</span> (AX-ID) är tom.</p>
        <p><strong>Resultat:</strong> Den specifika artikeln <em>hoppas över</em> (continue), men export fortsätter
          för övriga artiklar. Ordern markeras som <span class="mono">Exported = true</span> <strong>även om artiklar hoppar.</strong></p>
        <p><strong>Loggmeddelande:</strong> <em>"Ignoring article with supplier in export: [supplier]"</em></p>
        <p><strong>Fleet påverkas inte</strong> av detta — Fleet kräver inte leverantör per artikel.</p>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header warn">Scenario 11 — Specialhantering av WarehouseLocation "3" med egen leverantör (Fleet)</div>
      <div class="card-body">
        <p><strong>Villkor:</strong> Artikelns <span class="mono">WarehouseLocation == "3"</span> och artikeln har en egen <span class="mono">Supplier</span>.</p>
        <p><strong>Resultat:</strong> <span class="mono">SupplierNumber</span> i exportmeddelandet sätts till leverantörens <span class="mono">ExternalId</span> istället för den normalt beräknade <span class="mono">articleSupplierExternalId</span>. Om <span class="mono">WarehouseLocation == "3"</span> men ingen egen leverantör finns blir <span class="mono">SupplierNumber</span> tomt.</p>
      </div>
    </div>

    <div class="scenario-card">
      <div class="card-header warn">Scenario 12 — ExcludeFromWarehousePicking (Fleet)</div>
      <div class="card-body">
        <p><strong>Villkor:</strong> Artikeln har <span class="mono">article.ExcludeFromWarehousePicking == true</span>.</p>
        <p><strong>Resultat i exportmeddelandet:</strong></p>
        <ul>
          <li><span class="mono">ArticleNumber</span> ersätts med den fasta konstanten <span class="mono">AX.ArticleNumberExcludeFromWarehousePicking</span> istället för det genererade <span class="mono">adId</span></li>
          <li><span class="mono">SupplierNumber</span> blir tomt</li>
          <li><span class="mono">Warehouse</span> blir tomt</li>
          <li><span class="mono">PurchasePrice</span> blir <span class="mono">null</span></li>
          <li><span class="mono">Comment</span> blir <span class="mono">"[ArticleNumber] [Description]"</span> istället för den trimmade originalkommentaren</li>
        </ul>
      </div>
    </div>
  </div>

<!-- 7. ERRORS -->
  <div class="section" id="errors">
    <h2 class="section-title">7. Feltyper och exceptions</h2>
    <table>
      <thead><tr><th>Exceptiontyp</th><th>Felkod</th><th>Orsak</th><th>Var kastas den</th></tr></thead>
      <tbody>
        <tr>
          <td><span class="mono">UnableToExportException</span></td>
          <td><span class="mono">ORDER_WRONG_CLIENT_TYPE</span></td>
          <td>Ordern tillhör en annan klienttyp och kan inte exporteras.</td>
          <td><span class="mono">ExportOrderAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">NotFoundException</span></td>
          <td><span class="mono">ORDER_NOT_FOUND</span></td>
          <td>Ordern kunde inte hittas i databasen, vänligen kontakta support.</td>
          <td><span class="mono">ExportOrderAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">NotFoundException</span></td>
          <td><span class="mono">FLEET_ORDER_NOT_FOUND</span></td>
          <td>Ordern är inte en giltig Fleet-order.</td>
          <td><span class="mono">GetFleetOrderByIdAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">NotFoundException</span></td>
          <td><span class="mono">FLEET_ORDER_EXTERNAL_ID_NOT_FOUND</span></td>
          <td>Ingen matchande order hittades.</td>
          <td><span class="mono">GetFleetOrderByExternalIdAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">NotFoundException</span></td>
          <td><span class="mono">FLEET_ORDER_RETRIEVER_NOT_FOUND</span></td>
          <td>Ingen matchande order hittades.</td>
          <td><span class="mono">GetFleetOrderByExternalIdAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">UnableToExportException</span></td>
          <td><span class="mono">ORDER_NO_ARTICLES</span></td>
          <td>Ordern saknar artiklar och kan inte exporteras.</td>
          <td><span class="mono">ExportOrderAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">UnableToExportException</span></td>
          <td><span class="mono">ORDER_PRICES_NOT_SET</span></td>
          <td>Alla priser måste sättas innan export.</td>
          <td><span class="mono">ExportOrderAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">UnableToExportException</span></td>
          <td><span class="mono">ORDER_MISSING_CUSTOMER</span></td>
          <td>Kunduppgifter saknas för ordern.</td>
          <td><span class="mono">ExportOrderAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">UnableToExportException</span></td>
          <td><span class="mono">ORDER_NO_SALES_PRICE</span></td>
          <td>Försäljningspris saknas.</td>
          <td><span class="mono">ExportOrderAsync()</span></td>
        </tr>
        <tr>
          <td><span class="mono">UnableToExportException</span></td>
          <td><span class="mono">ARTICLE_NO_STOCK_INFO</span></td>
          <td>Lagersaldo saknas för en eller flera artiklar.</td>
          <td><span class="mono">CheckStockOrThrow()</span></td>
        </tr>
        <tr>
          <td><span class="mono">UnableToExportException</span></td>
          <td><span class="mono">ARTICLE_INSUFFICIENT_STOCK</span></td>
          <td>Otillräckligt lagersaldo för en eller flera artiklar.</td>
          <td><span class="mono">CheckStockOrThrow()</span></td>
        </tr>
        <tr>
          <td><span class="mono">Exception</span> (re-throw)</td>
          <td>-</td>
          <td>Okänt fel i exportprocessen</td>
          <td>catch-blocket i <span class="mono">ExportOrderAsync()</span></td>
        </tr>
      </tbody>
    </table>

    <div class="info-box">
      Alla kända fel kastas som <span class="mono">UnableToExportException</span> eller <span class="mono">NotFoundException</span>
      med en specifik felkod (t.ex. <span class="mono">ORDER_MISSING_CUSTOMER</span>). Felkoden mappas till ett
      användarvänligt meddelande i frontend enligt listan ovan. Alla exceptions kastas vidare (re-throw) och
      hanteras av API:ets globala middleware, som returnerar ett standardiserat felsvar till frontend.
    </div>
  </div>


  <!-- 8. CONFIGURATION -->
  <div class="section" id="config">
    <h2 class="section-title">8. Konfigurationskrav</h2>
    <p>
      API och AX Communication är separata projekt med skilda inställningar. Nycklarna nedan är grupperade efter ägare;
      kontrollera respektive projekts konfiguration vid felsökning. Inga hemliga värden dokumenteras här.
    </p>
    <h3 class="subsection">API-inställningar (ADonlineappAPI)</h3>
    <table>
      <thead><tr><th>Nyckel</th><th>Användning</th><th>Krav/hantering</th></tr></thead>
      <tbody>
        <tr><td><span class="mono">AXCommunicator:OrderSender:ConnectionString</span></td><td>API:ets anslutning för publicering till Service Bus</td><td><span class="badge badge-red">Obligatorisk</span> (Key Vault)</td></tr>
        <tr><td><span class="mono">AXCommunicator:OrderSender:Name</span></td><td>Namn på orderns sändkö</td><td><span class="badge badge-red">Obligatorisk</span> (Key Vault)</td></tr>
        <tr><td><span class="mono">AXCommunicator:OrderReciever:ConnectionString/Name</span></td><td>API:ets svarsmottagare; konfigurationsobjektet heter <span class="mono">AXCommunicatorConfiguration.OrderReciever</span></td><td><span class="badge badge-red">Obligatorisk</span> (Key Vault)</td></tr>
        <tr><td><span class="mono">AXCommunicator:SupplierFetcher:*</span></td><td>API:ets mottagning av leverantörslistan från AX Communication</td><td><span class="badge badge-red">Obligatorisk</span> (Key Vault)</td></tr>
        <tr><td><span class="mono">AXCommunicator:IdentificationNameStandard</span></td><td>Identifiering för Standard-meddelanden</td><td><span class="badge badge-red">Obligatorisk</span> (Key Vault)</td></tr>
        <tr><td><span class="mono">AXCommunicator:IdentificationNameCatalog</span></td><td>Identifiering för Catalog-meddelanden</td><td><span class="badge badge-red">Obligatorisk</span> (Key Vault)</td></tr>
        <tr><td><span class="mono">AXCommunicator:IdentificationNameFleet</span></td><td>Identifiering för Fleet-meddelanden</td><td><span class="badge badge-red">Obligatorisk</span> (Key Vault)</td></tr>
        <tr><td><span class="mono">AXCommunicator:OrderSender:MaxConcurrentCalls/PrefetchCount</span></td><td>Service Bus-sändar-/konsumentinställningar</td><td>Konfigureras per API-miljö</td></tr>
        <tr><td><span class="mono">AX:ArticleGroup</span></td><td>Standard</td><td><span class="badge badge-red">Obligatorisk</span></td></tr>
        <tr><td><span class="mono">AX:CatalogArticleGroup</span></td><td>Catalog</td><td><span class="badge badge-red">Obligatorisk</span></td></tr>
        <tr><td><span class="mono">AX:DefaultWarehouse</span></td><td>Standard, Catalog</td><td><span class="badge badge-red">Obligatorisk</span></td></tr>
        <tr><td><span class="mono">AX:CatalogWarehouse</span></td><td>Catalog</td><td><span class="badge badge-red">Obligatorisk</span></td></tr>
        <tr><td><span class="mono">AX:CatalogOrderWarehouse</span></td><td>Catalog</td><td><span class="badge badge-red">Obligatorisk</span></td></tr>
        <tr><td><span class="mono">AX:FleetArticleGroup</span></td><td>Fleet</td><td><span class="badge badge-orange">Krävs för Fleet</span></td></tr>
        <tr><td><span class="mono">AX:FleetWarehouse</span></td><td>Fleet-lager</td><td>Konfigurationsvärde för Fleet</td></tr>
        <tr><td><span class="mono">AX:InStockInventLocationId</span></td><td>Fleet-undantag för artikelgrupp 3 vid lagerkontroll</td><td>Konfigureras per API-miljö</td></tr>
        <tr><td><span class="mono">AX:FleetCustomerNumber</span></td><td>Fleet</td><td>Valfri (default: "0204")</td></tr>
        <tr><td><span class="mono">AX:FleetOrderTypeName</span></td><td>Fleet</td><td>Valfri (default: "FleetOrder")</td></tr>
      </tbody>
    </table>

    <p>
      <span class="mono">AX:*</span>-värdena används i exportpayloaden. Service Bus-anslutningar och köinställningar hämtas
      från Key Vault; lokalt kan Key Vault hoppas över om URL saknas.
    </p>

    
    <h3 class="subsection">Worker-felhantering</h3>
    <table>
      <thead><tr><th>Projekt/worker</th><th>Verifierat beteende vid fel</th></tr></thead>
      <tbody>
        <tr><td><span class="mono">ADonlineappAPI OrderWorkerService</span></td><td><span class="mono">AutoCompleteMessages = false</span>; fel abandonar meddelandet. Ingen explicit dead-letter-logik i workern; vidare leverans hanteras enligt Service Bus-konfigurationen.</td></tr>
        <tr><td><span class="mono">ADonlineappAPI SupplierWorkerService</span></td><td>Ogiltig/saknad integration dead-letteras direkt. Övriga fel försöks igen och dead-letteras efter 5 leveranser. Max auto-lock renewal 10 minuter; MaxConcurrentCalls = 1.</td></tr>
      </tbody>
    </table>

    <div class="warn-box">
      Konfigurationen är <strong>miljöspecifik</strong> — dev och produktion ska ha olika värden.
      Kontrollera alltid rätt miljö vid felsökning av konfigurationsproblem.
    </div>
  </div>


  <!-- 9. DIAGNOSTICS -->
  <div class="section" id="diagnostics">
    <h2 class="section-title">9. Snabbdiagnostik vid exportproblem</h2>

    <h3 class="subsection">Export startar inte alls?</h3>
    <ol>
      <li>Kontrollera om <span class="mono">order.Exporting == true</span> i databasen &mdash; om ja, vänta eller kontakta admin</li>
      <li>Kontrollera att alla <span class="mono">AX:*</span>-konfigurationer finns och har värden</li>
    </ol>

    <h3 class="subsection">Export misslyckas med prisfel?</h3>
    <ol>
      <li><strong>Standard/Catalog:</strong> Alla fyra priser måste vara ≥ 1 på varje artikel (SalesPrice, StoreSalesPrice, WorkshopSalesPrice, PurchasePrice)</li>
      <li><strong>Fleet:</strong> Bara SalesPrice behöver vara ≥ 1</li>
    </ol>

    <h3 class="subsection">Fleet-export misslyckas?</h3>
    <ol>
      <li>Kontrollera att ordern har en kopplad kund (med adressuppgifter)</li>
      <li>Kontrollera lagerstatus via ADI &mdash; Quantity kan vara för högt jämfört med lagervärdet</li>
      <li>Kontrollera om artikeln har egen leverantör eller är en artikelmall; dessa undantas från lagerkontrollen</li>
      <li>Kontrollera om artikelgruppen är <span class="mono">3</span>, som använder undantaget <span class="mono">InStockInventLocationId</span></li>
    </ol>

    <h3 class="subsection">Partiell export (Standard/Catalog)?</h3>
    <ol>
      <li>Kontrollera att varje artikel har en giltig leverantör</li>
      <li>Kontrollera att leverantörens <span class="mono">IsExcludedFromExport</span> är <span class="mono">false</span></li>
      <li>Kontrollera att leverantören har ett giltigt <span class="mono">Supplier.ExternalId</span> (AX-leverantörsnummer)</li>
    </ol>

    <h3 class="subsection">Hur verifierar man att meddelandet skickats och svaret kommit tillbaka?</h3>
    <p>
      Börja med API-projektets utgående kökonfiguration (<span class="mono">AXCommunicator:OrderSender</span>) och svarsmottagare
      (<span class="mono">AXCommunicatorConfiguration.OrderReciever</span>).<br />
      I AX Communication kontrollerar du <span class="mono">QueueConnections:OrderListener</span> för inkommande order,
      <span class="mono">OrderResultWorkerService</span> och dess SFTP-pollning var tionde sekund, samt
      <span class="mono">QueueConnections:OrderResultSender</span> för svarskön till API:t. För leverantörsimport kontrolleras
      <span class="mono">QueueConnections:SupplierListener</span>, <span class="mono">SupplierWorkerService</span> och
      <span class="mono">QueueConnections:SupplierSender</span>; API:ets <span class="mono">SupplierFetcher</span> tar emot listan.
    </p>

    <table>
      <thead><tr><th>Problem</th><th>Symptom</th><th>Trolig orsak</th><th>Åtgärd</th></tr></thead>
      <tbody>
        <tr>
          <td>Order fastnad i export</td>
          <td>order.Exporting = true även efter lång tid</td>
          <td>Applikationskrasch under export</td>
          <td>Kontrollera exportens finally-flöde i rätt klienttjänst innan åtgärd</td>
        </tr>
        <tr>
          <td>Order märks exporterad men når inte AX</td>
          <td>IO/FO saknas efter att exporten markerats som skickad</td>
          <td>Köpublicering, AX Communication, AX eller svarskonsumenten har inte slutfört sitt steg</td>
          <td>Kontrollera artikelantal, Service Bus-kön och AX Communication-loggar</td>
        </tr>
        <tr>
          <td>Artiklar saknas i AX-exporten</td>
          <td>Partiell export</td>
          <td>Leverantör saknas/exkluderad</td>
          <td>Kontrollera leverantör per artikel</td>
        </tr>
        <tr>
          <td>ADI-anrop misslyckas</td>
          <td>Fleet/Catalog-export kraschar</td>
          <td>ADI API ej nåbart</td>
          <td>Kontrollera ADI API-anslutning och loggar</td>
        </tr>
      </tbody>
    </table>
  </div>

</div><!-- end .container -->

<div class="doc-footer">
  ADOne &mdash; Exportflöde Dokumentation &bull; AD Sverige &bull; September 2026<br>
  <a href="/admin/documentation" target="_top" style="color:inherit;">← Tillbaka till huvuddokumentation</a>
</div>

<script>
  window.addEventListener('scroll', function() {
    document.getElementById('backToTop').style.display = window.pageYOffset > 300 ? 'block' : 'none';
  });

  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href^="#"]');
    if (!link) return;

    var target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
</script>
</body>
</html>

`;

const AdminDocumentationExport: FC = () => (
<iframe
  className="documentation-frame"
  title="ADOne systemdokumentation - Exportflöde"
  srcDoc={documentationHtml}
  style={{
    width: "100%",
    height: "calc(100vh - 120px)",
    border: 0,
    display: "block",
  }}
/>
);

export default AdminDocumentationExport;
